import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentMember, isAuthenticated, Member } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { Trophy, Medal, Award, CreditCard, Smartphone, AlertCircle, RefreshCw } from 'lucide-react';

const QRCardPage: React.FC = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [brightnessBoost, setBrightnessBoost] = useState(false);

  useEffect(() => {
    loadMemberData();
  }, []);

  // Keep screen awake while showing QR code
  useEffect(() => {
    if (member?.shopifyCustomerId && brightnessBoost) {
      let wakeLock: any = null;
      const requestWakeLock = async () => {
        try {
          if ('wakeLock' in navigator) {
            wakeLock = await (navigator as any).wakeLock.request('screen');
          }
        } catch {
          // Wake Lock API not supported or failed - that's okay
        }
      };
      requestWakeLock();

      return () => {
        if (wakeLock) {
          wakeLock.release();
        }
      };
    }
  }, [member?.shopifyCustomerId, brightnessBoost]);

  const loadMemberData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const data = await getCurrentMember();
      setMember(data);
      // Auto-enable brightness boost when page loads with valid QR
      if (data.shopifyCustomerId) {
        setBrightnessBoost(true);
      }
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-500';
      case 'Silver': return 'text-gray-400';
      default: return 'text-amber-700';
    }
  };

  const getTierBgGradient = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'from-yellow-500 to-yellow-600';
      case 'Silver': return 'from-gray-400 to-gray-500';
      default: return 'from-amber-700 to-amber-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading your card...</p>
        </div>
      </div>
    );
  }

  if (!member) return null;

  const TierIcon = getTierIcon(member.currentTier);
  const tierColor = getTierColor(member.currentTier);
  const tierGradient = getTierBgGradient(member.currentTier);
  const hasShopifyId = !!member.shopifyCustomerId;

  // Extract numeric ID from Shopify GID (e.g., "gid://shopify/Customer/23745322385780" -> "23745322385780")
  const shopifyNumericId = member.shopifyCustomerId?.split('/').pop() || '';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">My Card</h1>
                <p className="text-sm opacity-90">Show at checkout to earn points</p>
              </div>
              <div className="flex flex-col items-end">
                <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 mb-2 shadow-sm">
                  <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                  <span className="text-xs font-semibold text-buggy-dark">{member.currentTier} Tier</span>
                </div>
                <p className="text-xs opacity-75">Your Points</p>
                <p className="text-2xl font-bold">{member.currentPoints}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="max-w-md mx-auto p-4 space-y-4">

          {/* QR Card */}
          {hasShopifyId ? (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Card Header with Tier Accent */}
              <div className={`bg-gradient-to-r ${tierGradient} px-6 py-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard size={20} strokeWidth={2} className="text-white/90" />
                    <span className="text-white font-bold text-lg">InsiderPass</span>
                  </div>
                  <div className="text-right">
                    <p className="text-white/80 text-xs">Member</p>
                    <p className="text-white font-semibold">{member.firstName} {member.lastName}</p>
                  </div>
                </div>
              </div>

              {/* QR Code Area */}
              <div className="px-6 py-8 flex flex-col items-center">
                <div className="bg-white p-4 rounded-xl shadow-inner border-2 border-gray-100">
                  <QRCodeSVG
                    value={shopifyNumericId}
                    size={200}
                    level="M"
                    includeMargin={false}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                  />
                </div>
                <p className="text-xs text-buggy-gray mt-4 text-center">
                  Present this QR code to the cashier at checkout
                </p>
              </div>

              {/* Card Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-buggy-gray">Current Points</p>
                    <p className="text-lg font-bold text-buggy-blue">{member.currentPoints}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-buggy-gray">Tier Status</p>
                    <div className="flex items-center gap-1.5 justify-end">
                      <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                      <p className="text-lg font-bold text-buggy-dark">{member.currentTier}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* No Shopify ID State */
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} strokeWidth={2} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-buggy-dark mb-2">Card Setup In Progress</h3>
              <p className="text-sm text-buggy-gray mb-6">
                Your QR card is being set up. This usually takes just a moment.
                If this persists, please visit any BuggyBusters location and a team member can help get you set up.
              </p>
              <button
                onClick={() => {
                  setLoading(true);
                  loadMemberData();
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-buggy-blue text-white rounded-lg font-medium hover:bg-buggy-blue-light transition-colors"
              >
                <RefreshCw size={18} strokeWidth={2} />
                Check Again
              </button>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-white rounded-xl shadow-md p-5">
            <div className="flex items-center gap-2 mb-4">
              <Smartphone size={20} strokeWidth={2} className="text-buggy-blue" />
              <h3 className="font-bold text-buggy-dark">How to Use Your Card</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-buggy-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">1</span>
                </div>
                <p className="text-sm text-buggy-gray">Open this page when you're ready to check out</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-buggy-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">2</span>
                </div>
                <p className="text-sm text-buggy-gray">Show the QR code to the cashier</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 bg-buggy-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">3</span>
                </div>
                <p className="text-sm text-buggy-gray">They'll scan it to link your purchase and earn points automatically</p>
              </div>
            </div>
          </div>

          {/* Privacy Note */}
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-xs text-buggy-blue text-center">
              No need to share your phone number at checkout — just scan and go!
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRCardPage;
